import {
  TransactionCategoryLiteModel,
  TransactionCategoryModel,
  transactionCategoryKeys,
  useGetTransactionCategories,
  useGetTransactionCategory,
} from 'api-hooks/transaction-category';
import { OnChangeParams, Option } from 'baseui/select';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { Input } from 'components/elements';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useController } from 'react-hook-form';

function transformer(
  item: TransactionCategoryLiteModel | TransactionCategoryModel,
): Option {
  return {
    id: item.id,
    label: item.name,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterSelectItem?: (prevValue?: any, value?: any) => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: TransactionCategoryModel) => void;
  noMargin?: boolean;
  filter?: any;
  fieldType?: 'control' | 'uncontrol';
}

export default function TransactionCategorySelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:transaction_category'),
    placeholder = t('common:select_transaction_category'),
    filter,
  } = props;
  const { field } = useController({ name: props.name });

  const { navigate } = useNavigation();

  const { can } = useAuthorization();

  const { overrides, ...selectProps } = useSelectInputHelper({
    value: field.value,
    onSelectItem: (_value) => {
      field.onChange(_value[0]?.id);
      field.onBlur();
      props.onAfterSelectItem?.(field.value, _value[0]?.id);
    },
    useListQueryHook: useGetTransactionCategories,
    useDetailLazyQueryHook: useGetTransactionCategory,
    listKeyFunction: transactionCategoryKeys.index,
    detailKeyFunction: transactionCategoryKeys.view,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter,
        },
      }),
      [filter],
    ),
    renderCreate: !!can(AuthorizationRules.TransactionCategoriesCreate),
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.TransactionCategoriesCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, TransactionCategoryModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      fieldType={props.fieldType}
      overrides={overrides}
      {...selectProps}
    />
  );
}
